import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Table,
  Badge,
  Modal,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import {
  getDiabetesCareRegistrations,
  updateDiabetesCareStatus,
  deleteDiabetesCareRegistration,
} from "../services/firebaseService";

const DiabetesCareAdmin = ({ onBack }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [actionType, setActionType] = useState("");

  // Hardcoded credentials for diabetes care admin
  const ADMIN_CREDENTIALS = {
    username: "fayrouzadmin",
    password: "7777",
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRegistrations();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError("");

    if (
      loginForm.username === ADMIN_CREDENTIALS.username &&
      loginForm.password === ADMIN_CREDENTIALS.password
    ) {
      setIsAuthenticated(true);
    } else {
      setLoginError("اسم المستخدم أو كلمة المرور غير صحيحة");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setLoginForm({ username: "", password: "" });
    setRegistrations([]);
  };

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDiabetesCareRegistrations();
      setRegistrations(data);
    } catch (err) {
      console.error("Error fetching diabetes care registrations:", err);
      setError("فشل في تحميل التسجيلات. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (registration, newStatus) => {
    setSelectedRegistration(registration);
    setActionType("status-" + newStatus);
    setShowConfirmModal(true);
  };

  const handleDeleteRegistration = (registration) => {
    setSelectedRegistration(registration);
    setActionType("delete");
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    setShowConfirmModal(false);

    try {
      if (actionType === "delete") {
        await deleteDiabetesCareRegistration(selectedRegistration.id);
        fetchRegistrations();
      } else if (actionType.startsWith("status-")) {
        const newStatus = actionType.split("-")[1];
        await updateDiabetesCareStatus(selectedRegistration.id, newStatus);
        fetchRegistrations();
      }
    } catch (err) {
      console.error("Error performing action:", err);
      setError(
        `فشل في ${
          actionType.includes("status") ? "تحديث الحالة" : "حذف التسجيل"
        }. يرجى المحاولة مرة أخرى.`
      );
    }
  };

  const filteredRegistrations =
    statusFilter === "all"
      ? registrations
      : registrations.filter((r) => r.status === statusFilter);

  const formatDate = (dateString) => {
    if (!dateString) return "غير متوفر";
    return new Date(dateString).toLocaleString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "warning", text: "dark", label: "قيد الانتظار" },
      contacted: { bg: "info", text: "white", label: "تم التواصل" },
      scheduled: { bg: "primary", text: "white", label: "تم جدولة الموعد" },
      completed: { bg: "success", text: "white", label: "مكتمل" },
      cancelled: { bg: "secondary", text: "white", label: "ملغي" },
    };

    const config = statusConfig[status] || {
      bg: "light",
      text: "dark",
      label: status,
    };

    return (
      <Badge bg={config.bg} text={config.text}>
        {config.label}
      </Badge>
    );
  };

  // Login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div
        style={{
          direction: "rtl",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={6} lg={4}>
              <Card
                className="shadow-lg border-0"
                style={{ borderRadius: "20px" }}
              >
                <Card.Header
                  className="text-center text-white"
                  style={{
                    background:
                      "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    borderRadius: "20px 20px 0 0",
                    padding: "20px",
                  }}
                >
                  <h4 className="mb-0">
                    <i className="bi bi-shield-lock me-2"></i>
                    إدارة رعاية مرضى السكري
                  </h4>
                </Card.Header>
                <Card.Body className="p-4">
                  {loginError && (
                    <Alert variant="danger" className="text-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {loginError}
                    </Alert>
                  )}

                  <Form onSubmit={handleLogin}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        <i className="bi bi-person me-2"></i>
                        اسم المستخدم
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={loginForm.username}
                        onChange={(e) =>
                          setLoginForm({
                            ...loginForm,
                            username: e.target.value,
                          })
                        }
                        placeholder="أدخل اسم المستخدم"
                        required
                        style={{ borderRadius: "10px", padding: "12px 15px" }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        <i className="bi bi-key me-2"></i>
                        كلمة المرور
                      </Form.Label>
                      <Form.Control
                        type="password"
                        value={loginForm.password}
                        onChange={(e) =>
                          setLoginForm({
                            ...loginForm,
                            password: e.target.value,
                          })
                        }
                        placeholder="أدخل كلمة المرور"
                        required
                        style={{ borderRadius: "10px", padding: "12px 15px" }}
                      />
                    </Form.Group>

                    <div className="d-grid gap-2">
                      <Button
                        type="submit"
                        style={{
                          background:
                            "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                          border: "none",
                          borderRadius: "25px",
                          padding: "12px 30px",
                          fontWeight: "600",
                        }}
                      >
                        <i className="bi bi-box-arrow-in-right me-2"></i>
                        تسجيل الدخول
                      </Button>

                      <Button
                        variant="outline-secondary"
                        onClick={onBack}
                        style={{ borderRadius: "25px", padding: "12px 30px" }}
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        العودة للصفحة الرئيسية
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Admin dashboard if authenticated
  return (
    <div
      style={{
        direction: "rtl",
        minHeight: "100vh",
        backgroundColor: "#f8f9fa",
      }}
    >
      <Container className="py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-primary">
            <i className="bi bi-clipboard-heart me-2"></i>
            إدارة رعاية مرضى السكري
          </h2>
          <div>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={onBack}
            >
              <i className="bi bi-arrow-left me-2"></i>
              العودة للصفحة الرئيسية
            </Button>
            <Button variant="outline-danger" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right me-2"></i>
              تسجيل الخروج
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {/* Controls */}
        <Row className="mb-4">
          <Col md={6}>
            <Button variant="primary" onClick={fetchRegistrations}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              تحديث البيانات
            </Button>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">تصفية حسب الحالة</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">جميع التسجيلات</option>
                <option value="pending">قيد الانتظار</option>
                <option value="contacted">تم التواصل</option>
                <option value="scheduled">تم جدولة الموعد</option>
                <option value="completed">مكتمل</option>
                <option value="cancelled">ملغي</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          {["pending", "contacted", "scheduled", "completed"].map((status) => {
            const count = registrations.filter(
              (r) => r.status === status
            ).length;
            const statusLabels = {
              pending: "قيد الانتظار",
              contacted: "تم التواصل",
              scheduled: "مجدولة",
              completed: "مكتملة",
            };

            return (
              <Col md={3} key={status}>
                <Card className="text-center mb-3">
                  <Card.Body>
                    <h3 className="text-primary">{count}</h3>
                    <p className="mb-0">{statusLabels[status]}</p>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* Data Table */}
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">جاري تحميل التسجيلات...</p>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <Alert variant="info">
            <i className="bi bi-info-circle me-2"></i>
            لا توجد تسجيلات
            {statusFilter !== "all" ? ` بحالة "${statusFilter}"` : ""}.
          </Alert>
        ) : (
          <Card>
            <Card.Body className="p-0">
              <Table responsive striped hover className="mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>تاريخ التسجيل</th>
                    <th>اسم المريض</th>
                    <th>رقم الهاتف</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegistrations.map((registration) => (
                    <tr key={registration.id}>
                      <td>{formatDate(registration.registeredAt?.toDate())}</td>
                      <td>
                        <strong>{registration.name}</strong>
                        <br />
                        <small className="text-muted">مركز الفيروز الطبي</small>
                      </td>
                      <td>
                        <a
                          href={`tel:${registration.phone}`}
                          className="text-decoration-none"
                        >
                          <i className="bi bi-telephone me-1"></i>
                          {registration.phone}
                        </a>
                      </td>
                      <td>{getStatusBadge(registration.status)}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {registration.status === "pending" && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() =>
                                handleStatusChange(registration, "contacted")
                              }
                            >
                              تم التواصل
                            </Button>
                          )}
                          {registration.status === "contacted" && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                handleStatusChange(registration, "scheduled")
                              }
                            >
                              جدولة موعد
                            </Button>
                          )}
                          {(registration.status === "scheduled" ||
                            registration.status === "contacted") && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() =>
                                handleStatusChange(registration, "completed")
                              }
                            >
                              مكتمل
                            </Button>
                          )}
                          {registration.status !== "cancelled" && (
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() =>
                                handleStatusChange(registration, "cancelled")
                              }
                            >
                              إلغاء
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() =>
                              handleDeleteRegistration(registration)
                            }
                          >
                            حذف
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Confirmation Modal */}
        <Modal
          show={showConfirmModal}
          onHide={() => setShowConfirmModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>تأكيد الإجراء</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionType === "delete" ? (
              <p>
                هل أنت متأكد من حذف تسجيل{" "}
                <strong>{selectedRegistration?.name}</strong>؟
              </p>
            ) : actionType === "status-contacted" ? (
              <p>
                تأكيد التواصل مع <strong>{selectedRegistration?.name}</strong>؟
              </p>
            ) : actionType === "status-scheduled" ? (
              <p>
                تأكيد جدولة موعد لـ{" "}
                <strong>{selectedRegistration?.name}</strong>؟
              </p>
            ) : actionType === "status-completed" ? (
              <p>
                تأكيد اكتمال الفحص لـ{" "}
                <strong>{selectedRegistration?.name}</strong>؟
              </p>
            ) : actionType === "status-cancelled" ? (
              <p>
                تأكيد إلغاء تسجيل <strong>{selectedRegistration?.name}</strong>؟
              </p>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              إلغاء
            </Button>
            <Button
              variant={actionType === "delete" ? "danger" : "primary"}
              onClick={confirmAction}
            >
              تأكيد
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default DiabetesCareAdmin;
